import { useEffect } from "react";

type Props = {
  ga4Id: string | null;
  clarityId: string | null;
};

/**
 * Loads GA4 + Microsoft Clarity AFTER the page is interactive.
 * Keeps these third-party scripts off the critical path so they
 * don't hurt LCP / TBT in PageSpeed Insights.
 */
export function DeferredAnalytics({ ga4Id, clarityId }: Props) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let started = false;

    const boot = () => {
      if (started) return;
      started = true;

      if (ga4Id) {
        const s = document.createElement("script");
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
        document.head.appendChild(s);
        const w = window as any;
        w.dataLayer = w.dataLayer || [];
        const gtag = (...args: unknown[]) => { w.dataLayer.push(args); };
        gtag("js", new Date());
        gtag("config", ga4Id);
        w.gtag = gtag;
      }


      if (clarityId) {
        (function (c: any, l: Document, a: string, r: string, i: string) {
          c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
          const t = l.createElement(r) as HTMLScriptElement;
          t.async = true;
          t.src = "https://www.clarity.ms/tag/" + i;
          const y = l.getElementsByTagName(r)[0];
          y.parentNode!.insertBefore(t, y);
        })(window, document, "clarity", "script", clarityId);
      }
    };

    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    const timer = ric
      ? ric(boot, { timeout: 4000 })
      : window.setTimeout(boot, 2500);

    // Boot on first interaction too, in case idle never fires.
    const onInteract = () => boot();
    window.addEventListener("pointerdown", onInteract, { once: true, passive: true });
    window.addEventListener("keydown", onInteract, { once: true });
    window.addEventListener("scroll", onInteract, { once: true, passive: true });

    return () => {
      if (ric) (window as any).cancelIdleCallback?.(timer);
      else clearTimeout(timer as number);
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
      window.removeEventListener("scroll", onInteract);
    };
  }, [ga4Id, clarityId]);

  return null;
}
